/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { Layout, Button, Form, Input, notification } from 'antd';
import { connect } from 'react-redux';
import { signIn } from '../../redux/actions/authActions';
import get from 'lodash/get';
import { bool, func } from 'prop-types';
import logoCrc from '../../assets/logoCrc.png';
import history from '../../services/history';
import { EyeInvisibleOutlined, EyeOutlined, CheckCircleFilled } from '@ant-design/icons';
import * as constants from '../../utils/constants';
import { userSignIn, userSignOut, sendLoginOtp, verifyLoginOtp } from '../../services/auth';

const Login = ({ onSignIn, showLoader, authChallenge }) => {
  const [loginForm] = Form.useForm();

  // OTP state management (Data360-style)
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [credentials, setCredentials] = useState(null);

  // OTP input refs
  const otpRefs = useRef([]);

  // Timer effect for OTP resend
  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else if (timer === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timer, canResend]);

  useEffect(() => {
    if (authChallenge !== '') {
      if (authChallenge === constants.CHALLENGE_NAME) {
        const formValues = loginForm.getFieldsValue();
        history.push('/reset-password', {
          email: formValues.username || credentials?.username || '',
          isRedirectedFromLogin: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChallenge]);

  // Handle sending OTP
  const handleSendOtp = async (email) => {
    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    try {
      setIsSendingOtp(true);
      setLocalError(null);
      const response = await sendLoginOtp({ email });
      console.log('OTP send response:', response);

      // Set OTP sent state immediately to show OTP fields
      setOtpSent(true);
      setTimer(30);
      setCanResend(false);
      notification.success({ message: 'OTP sent to your email' });

      // Focus first OTP input
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      console.error('Error sending OTP:', err);
      const errorMsg =
        err.message || err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setLocalError(errorMsg);
      notification.error({ message: errorMsg });
      setOtpSent(false); // Make sure it's false on error
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle resending OTP
  const handleResendOtp = async () => {
    if (!canResend || !credentials) return;
    await handleSendOtp(credentials.username);
  };

  // Handle OTP verification and login
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setLocalError('Please enter a valid 6-digit OTP');
      return;
    }

    if (!credentials) {
      setLocalError('Credentials not found. Please start over.');
      setOtpSent(false);
      return;
    }

    try {
      setIsVerifying(true);
      setLocalError(null);
      const res = await verifyLoginOtp({
        email: credentials.username,
        code: otp,
      });

      // Handle response format: { success: 1, data: { success: true, ... } }
      const isValid = res?.data?.success || res?.data?.valid || res?.success === 1;

      if (isValid) {
        setOtpVerified(true);
        notification.success({ message: 'OTP verified successfully' });
        // Clear OTP validation flag before actual login
        sessionStorage.removeItem('otp_validation_in_progress');
        // After OTP verification, proceed with login
        await onSignIn(credentials);
      } else {
        const errorMsg = 'Invalid OTP. Please try again.';
        setLocalError(errorMsg);
        notification.error({ message: errorMsg });
        setOtp('');
        setTimeout(() => {
          otpRefs.current[0]?.focus();
        }, 0);
      }
    } catch (err) {
      const errorMsg = err.message || 'Verification failed. Please retry.';
      setLocalError(errorMsg);
      notification.error({ message: errorMsg });
      setOtp('');
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 0);
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle form submit - validate credentials first, then send OTP
  const handleSubmit = async (values) => {
    // Ant Design Form.onFinish passes form values directly, not an event
    const email = values.username.toLowerCase().trim();
    const password = values.password;

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    // If OTP is already sent, don't send again
    if (otpSent) {
      return;
    }

    // Store credentials for later use
    setCredentials({ username: email, password });

    // Validate credentials BEFORE sending OTP
    try {
      setIsValidating(true);
      setLocalError(null);

      // Set flag to prevent redirects during OTP validation
      sessionStorage.setItem('otp_validation_in_progress', 'true');

      // Attempt to sign in to validate credentials
      const result = await userSignIn(email, password);

      // Check if there's a challenge (like NEW_PASSWORD_REQUIRED)
      if (result && result.challengeName) {
        // Handle challenge - this will be caught by the authChallenge useEffect
        setIsValidating(false);
        return;
      }

      // If sign in succeeds without challenge, immediately sign out to prevent auto-login
      // We only wanted to validate, not actually log in yet
      // Clear tokens immediately to prevent redirects
      localStorage.removeItem('ap_idToken');
      localStorage.removeItem('ap_rToken');
      localStorage.removeItem('user');

      try {
        await userSignOut();
      } catch (signOutErr) {
        // Ignore sign out errors - we just want to clear the session
        console.log('Sign out during validation:', signOutErr);
      }

      // Small delay to ensure sign out completes and Hub events fire
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Keep OTP flag set - don't remove until OTP is verified or user cancels
      // Set OTP sent state immediately to prevent any redirects
      // Credentials are valid, now send OTP
      setIsValidating(false);
      await handleSendOtp(email);
    } catch (err) {
      // Credentials are invalid, show error
      let errorMessage = 'Invalid username or password';
      if (err.message) {
        if (err.message.includes('Incorrect username or password')) {
          errorMessage = 'Incorrect username or password';
        } else if (err.message.includes('User does not exist')) {
          errorMessage = 'User does not exist';
        } else if (err.message.includes('User is not confirmed')) {
          errorMessage = 'Please verify your email address first';
        } else {
          errorMessage = err.message;
        }
      }
      setLocalError(errorMessage);
      notification.error({ message: errorMessage });
      // Clear OTP flag on error
      sessionStorage.removeItem('otp_validation_in_progress');
    } finally {
      setIsValidating(false);
    }
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/, '');
    const newOtp = otp.split('');
    newOtp[index] = sanitizedValue;
    const updatedOtp = newOtp.join('');
    setOtp(updatedOtp);
    setLocalError(null);

    if (sanitizedValue && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    setLocalError(null);
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      otpRefs.current[focusIndex]?.focus();
    }, 0);
  };

  // Reset OTP flow
  const handleResetOtp = () => {
    setOtpSent(false);
    setOtp('');
    setOtpVerified(false);
    setTimer(0);
    setCanResend(true);
    setLocalError(null);
    setCredentials(null);
    // Clear OTP validation flag
    sessionStorage.removeItem('otp_validation_in_progress');
  };

  const isLoading = showLoader || isSendingOtp || isValidating;

  return (
    <Layout.Content className="h-100">
      <div className="d-flex h-100 w-100 overflow-hidden">
        <div className="d-fill overflow-auto w-50 login-bg">
          <div className="p-4 position-relative ">
            <a href="aside-logo" className="login-logo">
              <img src={logoCrc} alt="logo" className="logo_main" />
            </a>
          </div>
        </div>
        <div className="d-fill w-50 overflow-auto">
          <div className="login-form">
            <h1>Welcome to CRCE-Connect</h1>

            <Form
              form={loginForm}
              name="normal_login"
              initialValues={{ remember: true }}
              onFinish={handleSubmit}
            >
              {/* Error message */}
              {localError && (
                <div
                  style={{
                    color: '#ff4d4f',
                    fontSize: '14px',
                    marginBottom: '16px',
                    textAlign: 'center',
                  }}
                >
                  {localError}
                </div>
              )}

              {/* Email/Password fields - always visible, disabled after OTP sent */}
              {!otpSent && (
                <>
                  <Form.Item
                    name="username"
                    rules={[
                      { required: true, message: 'Please input your email address!' },
                      { pattern: constants.EMAIL_PATTERN, message: 'Provide a valid email.' },
                    ]}
                    style={{ color: '#ef314c', fontSize: '14px' }}
                  >
                    <Input size="large" placeholder="Email address" disabled={isLoading} />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: 'Please input your Password!' }]}
                    style={{ color: '#ef314c', fontSize: '14px', marginBottom: 0 }}
                  >
                    <Input.Password
                      size="large"
                      type="password"
                      placeholder="Password"
                      disabled={isLoading}
                      iconRender={(visible) =>
                        visible ? (
                          <EyeOutlined className="password_icon" />
                        ) : (
                          <EyeInvisibleOutlined className="password_icon" />
                        )
                      }
                    />
                  </Form.Item>
                  <div className="d-flex pb-4 fgt-pswd">
                    <Form.Item noStyle />
                  </div>

                  {/* Sign In Button */}
                  <Form.Item>
                    <Button
                      size="large"
                      shape="round"
                      block
                      type="primary"
                      htmlType="submit"
                      className="login-form-button primary-action"
                      disabled={isLoading}
                      loading={isLoading}
                    >
                      {isLoading ? (isValidating ? 'Validating...' : 'Sending OTP...') : 'Sign In'}
                    </Button>
                  </Form.Item>
                </>
              )}

              {/* OTP Section - shown after OTP is sent */}
              {otpSent && !otpVerified && credentials && (
                <div className="otp-section">
                  <div className="otp-header">
                    <span className="otp-label">Enter Verification Code</span>
                    <span className="otp-sent-badge">
                      <CheckCircleFilled /> Sent
                    </span>
                  </div>
                  <p className="otp-hint" style={{ marginBottom: '16px' }}>
                    We&apos;ve sent a 6-digit OTP to{' '}
                    <strong style={{ color: '#ef314c' }}>{credentials?.username}</strong>
                  </p>

                  {/* OTP Input Boxes */}
                  <div className="otp-input-group">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={otp[index] || ''}
                        className={`otp-box ${otp[index] ? 'filled' : ''}`}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        disabled={isVerifying}
                      />
                    ))}
                  </div>

                  {/* Resend OTP */}
                  <div className="resend-section">
                    <span className="resend-text">Didn&apos;t receive code?</span>
                    <Button
                      type="link"
                      size="small"
                      className="resendBtn"
                      disabled={!canResend || isVerifying}
                      onClick={handleResendOtp}
                    >
                      {canResend ? 'Resend OTP' : `Resend in ${timer}s`}
                    </Button>
                  </div>

                  {/* Verify OTP Button */}
                  <Form.Item>
                    <Button
                      size="large"
                      shape="round"
                      block
                      type="primary"
                      className="login-form-button primary-action"
                      loading={isVerifying || showLoader}
                      onClick={handleVerifyOtp}
                      disabled={otp.length !== 6 || isVerifying || showLoader}
                    >
                      {isVerifying
                        ? 'Verifying...'
                        : showLoader
                        ? 'Signing In...'
                        : 'Verify & Sign In'}
                    </Button>
                  </Form.Item>

                  {/* Back button */}
                  <Button
                    block
                    className="login-form-button"
                    onClick={handleResetOtp}
                    disabled={isVerifying || showLoader}
                  >
                    Back to Login
                  </Button>
                </div>
              )}

              {/* OTP Verified - signing in */}
              {otpVerified && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ color: '#52c41a', fontSize: '14px' }}>
                    OTP verified successfully. Signing in...
                  </div>
                </div>
              )}
            </Form>
            <span className="login_copy">
              Pioneered by CRC Evans &copy;{new Date().getFullYear()} |{' '}
              <a href="" rel="noreferrer" target="_blank" style={{ pointerEvents: 'none' }}>
                Terms & Conditions
              </a>{' '}
              |{' '}
              <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
                Privacy Policy
              </a>
            </span>
          </div>
        </div>
      </div>
    </Layout.Content>
  );
};

Login.propTypes = {
  onSignIn: func.isRequired,
  showLoader: bool.isRequired,
};

const mapStateToProps = ({ auth }) => {
  const showLoader = get(auth, 'isLoginLoading', false);
  const authChallenge = get(auth, 'challengeName', '');
  return {
    showLoader,
    authChallenge,
  };
};

const mapDispatchToProps = {
  onSignIn: signIn,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
